'use client'

import Image from 'next/image'
import { BookOpen, MapPin } from 'lucide-react'
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { CityAreaMapCameraPadding } from '@/lib/mock/cities'
import { getRouteMidpoint, getRouteZoomBetweenPoints, ROUTE_ZOOM_MAX } from '@/lib/geo'
import {
  addMarkerLabelPadding,
  addRouteMarkerLabelPadding,
  applyCenterLngOffset,
  applyAreaMapColors,
  canFitMapBounds,
  clampMapBoundsPadding,
  CITY_MARKETING_MAP_PALETTE,
  DESKTOP_MAP_BREAKPOINT_PX,
  getDesktopMapZoomOffset,
  getHorizontalPaddingForFocus,
  getResponsiveMapFocusPadding,
  MAP_TRANSITION_MS,
  MARKER_PILL_CLASS,
  MARKER_PILL_MAX_WIDTH_PX,
  type MapBoundsPadding,
} from '@/components/Location/LocationAreaMap'
import { LocationMapHoverCard } from '@/components/Location/LocationMapHoverCard'
import {
  LocationMapNavigationHandlers,
  shouldSkipCameraFit,
  useLocationMapContainerResizeObserver,
} from '@/components/Location/LocationMapNavigation'
import { MapContentChrome } from '@/components/Location/MapContentChrome'
import { MapHoverCardPortal } from '@/components/Location/MapHoverCardPortal'
import { Map as MapCanvas, MapArc, MapMarker, MarkerContent, useMap } from '@/components/ui/map'
import { useMessages } from '@/lib/i18n/useMessages'
import type { MapLocationCardData } from '@/lib/locations/mapLocationData'
import { formatMobileMapMarkerLabel } from '@/lib/locations/formatMapMarkerLabel'
import type { LngLat } from '@/lib/mock/mapLocations'
import { cn } from '@/utilities/ui'
import maplibregl from 'maplibre-gl'

type LocationMapHeroMarker = {
  latitude: number
  longitude: number
  name: string
  slug: string
}

function useCompactMapMarkerLabels() {
  const [useCompactLabels, setUseCompactLabels] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${DESKTOP_MAP_BREAKPOINT_PX - 1}px)`)
    const update = () => setUseCompactLabels(media.matches)

    update()
    media.addEventListener('change', update)

    return () => media.removeEventListener('change', update)
  }, [])

  return useCompactLabels
}

function resolveMapMarkerDisplayLabel(name: string, useCompactLabels: boolean): string {
  return useCompactLabels ? formatMobileMapMarkerLabel(name) : name
}

export type LocationMapHeroRouteDirection =
  | 'campus-to-university'
  | 'university-to-campus'

type LocationMapHeroMapProps = {
  center: LngLat
  fallbackImage: string
  fallbackImageAlt: string
  locationCards: MapLocationCardData[]
  mapCameraPadding?: CityAreaMapCameraPadding
  mapControlLabels: {
    zoomGroupLabel: string
    zoomInLabel: string
    zoomOutLabel: string
  }
  markers: LocationMapHeroMarker[]
  routeMarker?: {
    latitude: number
    longitude: number
  }
  routeAnimationId?: number
  routeDirection?: LocationMapHeroRouteDirection
  selectedCampusSlug?: string
  universityCoordinate?: LngLat
  universityName?: string
  zoom: number
}

function LocationMapHeroMapStyle() {
  const { isLoaded, map } = useMap()

  useEffect(() => {
    if (!map || !isLoaded) return

    const applyColors = () => {
      applyAreaMapColors(map, CITY_MARKETING_MAP_PALETTE)
    }

    applyColors()
    map.on('styledata', applyColors)

    return () => {
      map.off('styledata', applyColors)
    }
  }, [isLoaded, map])

  return null
}

function LocationMapHeroMapErrorWatcher({ onError }: { onError: () => void }) {
  const { map } = useMap()

  useEffect(() => {
    if (!map) return

    const handleError = () => onError()

    map.on('error', handleError)

    return () => {
      map.off('error', handleError)
    }
  }, [map, onError])

  return null
}

function normalizeLngLat(center: maplibregl.LngLatLike | undefined, fallback: LngLat): LngLat {
  if (!center) {
    return fallback
  }

  if (Array.isArray(center)) {
    return [center[0], center[1]]
  }

  if (typeof center === 'object' && 'lng' in center && 'lat' in center) {
    return [center.lng, center.lat]
  }

  return fallback
}

function getCameraStateKey(center: LngLat, zoom: number): string {
  return `${center[0].toFixed(5)},${center[1].toFixed(5)}-${zoom.toFixed(2)}`
}

const MARKER_PILL_HALF_WIDTH_PX = MARKER_PILL_MAX_WIDTH_PX / 2
const MARKER_PILL_HEIGHT_PX = 40
const ZERO_MAP_PADDING: MapBoundsPadding = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
}

function getMarkerPillRect(map: maplibregl.Map, coordinate: LngLat) {
  const point = map.project(coordinate)

  return {
    left: point.x - MARKER_PILL_HALF_WIDTH_PX,
    right: point.x + MARKER_PILL_HALF_WIDTH_PX,
    top: point.y - MARKER_PILL_HEIGHT_PX,
    bottom: point.y,
  }
}

/** Whether any two marker pills would visually overlap at the map's current camera state. */
function pillsOverlap(map: maplibregl.Map, points: LngLat[]): boolean {
  const rects = points.map((point) => getMarkerPillRect(map, point))

  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      const a = rects[i]
      const b = rects[j]
      const overlapsHorizontally = a.left < b.right && b.left < a.right
      const overlapsVertically = a.top < b.bottom && b.top < a.bottom

      if (overlapsHorizontally && overlapsVertically) {
        return true
      }
    }
  }

  return false
}

const PILL_OVERLAP_ZOOM_STEP = 0.25
const PILL_OVERLAP_MAX_ITERATIONS = 32

/**
 * Zooms in incrementally (re-fitting bounds each step) until two marker pills
 * no longer overlap on screen — a safety net for pairs close enough that the
 * distance/bounds formulas alone don't guarantee visual separation.
 */
function resolveZoomToAvoidPillOverlap(
  map: maplibregl.Map,
  bounds: maplibregl.LngLatBounds,
  points: LngLat[],
  padding: MapBoundsPadding,
  initialCenter: LngLat,
  initialZoom: number,
  maxZoom: number,
  lockCenter?: LngLat,
): { center: LngLat; zoom: number } {
  if (points.length < 2) {
    return { center: lockCenter ?? initialCenter, zoom: initialZoom }
  }

  const previousCenter = map.getCenter()
  const previousZoom = map.getZoom()
  const previousPadding = map.getPadding()
  let center = lockCenter ?? initialCenter
  let zoom = initialZoom

  for (let iteration = 0; iteration < PILL_OVERLAP_MAX_ITERATIONS; iteration++) {
    map.jumpTo({ center, padding: ZERO_MAP_PADDING, zoom })

    if (!pillsOverlap(map, points) || zoom >= maxZoom) {
      break
    }

    zoom = Math.min(maxZoom, zoom + PILL_OVERLAP_ZOOM_STEP)

    if (!lockCenter) {
      const refit = map.cameraForBounds(bounds, { padding, maxZoom: zoom })

      if (refit?.center) {
        center = refit.center as LngLat
      }
    }
  }

  // The temporary jumps above are only for measuring label overlap. Restore
  // the live camera so the caller's flyTo starts from the current view.
  map.jumpTo({
    center: previousCenter,
    padding: previousPadding,
    zoom: previousZoom,
  })

  return { center: lockCenter ?? center, zoom }
}

type HeroMapCameraProps = {
  center: LngLat
  mapCameraPadding?: CityAreaMapCameraPadding
  markers: LocationMapHeroMarker[]
  routeMarker?: {
    latitude: number
    longitude: number
  }
  universityCoordinate?: LngLat
  zoom: number
}

type LocationMapHeroMapCameraProps = HeroMapCameraProps & {
  onCameraAnimationComplete?: (animationKey: string) => void
  preserveUserZoom?: boolean
  routeAnimationKey?: string
}

function inflateRouteBounds(
  universityCoordinate: LngLat,
  campusCoordinate: LngLat,
): maplibregl.LngLatBounds {
  const bounds = new maplibregl.LngLatBounds()

  bounds.extend(universityCoordinate)
  bounds.extend(campusCoordinate)

  const lngSpan = Math.max(bounds.getEast() - bounds.getWest(), 0.001)
  const latSpan = Math.max(bounds.getNorth() - bounds.getSouth(), 0.001)
  // Minimum buffer stays small so very close pairs (e.g. <1km apart) aren't
  // forced into an artificially large bounding box that zooms out too far.
  const bufferLng = Math.max(0.006, lngSpan * 0.55)
  const bufferLat = Math.max(0.005, latSpan * 0.5)

  bounds.extend([bounds.getWest() - bufferLng, bounds.getSouth() - bufferLat])
  bounds.extend([bounds.getEast() + bufferLng, bounds.getNorth() + bufferLat])

  return bounds
}

function getRouteCamera(
  map: maplibregl.Map,
  universityCoordinate: LngLat,
  campusCoordinate: LngLat,
  padding: MapBoundsPadding,
  baseZoom: number,
): {
  cameraCenter: LngLat
  cameraZoom: number
  flyPadding: MapBoundsPadding
} {
  const bounds = inflateRouteBounds(universityCoordinate, campusCoordinate)
  const routeMidpoint = getRouteMidpoint(universityCoordinate, campusCoordinate)
  const distanceZoom = getRouteZoomBetweenPoints(universityCoordinate, campusCoordinate, baseZoom)
  const container = map.getContainer()
  let fitPadding = padding
  let fitCamera = map.cameraForBounds(bounds, { padding: fitPadding })

  if (!fitCamera?.center) {
    fitPadding = clampMapBoundsPadding(container.clientWidth, container.clientHeight, padding)
    fitCamera = map.cameraForBounds(bounds, { padding: fitPadding })
  }

  if (!fitCamera?.center) {
    const isNarrowViewport = container.clientWidth < DESKTOP_MAP_BREAKPOINT_PX
    fitPadding = {
      top: 48,
      bottom: 48,
      ...(isNarrowViewport
        ? { left: 32, right: 32 }
        : getHorizontalPaddingForFocus(container.clientWidth, 48)),
    }
    fitCamera = map.cameraForBounds(bounds, { padding: fitPadding })
  }

  if (!fitCamera?.center) {
    return {
      cameraCenter: routeMidpoint,
      cameraZoom: Math.max(container.clientWidth < 768 ? 8.5 : 9, baseZoom - 1.5),
      flyPadding: fitPadding,
    }
  }

  const fitZoom = fitCamera.zoom ?? distanceZoom
  const isNarrowViewport = container.clientWidth < 768
  const routeZoomMargin = isNarrowViewport ? 0 : 1
  const routeZoomFloor = isNarrowViewport ? 8.5 : 9
  const fitBasedZoom = fitZoom - routeZoomMargin
  const labelSeparationZoomFloor = isNarrowViewport ? distanceZoom - 2 : routeZoomFloor
  const cameraZoom = Math.min(
    distanceZoom,
    Math.max(routeZoomFloor, labelSeparationZoomFloor, fitBasedZoom),
  )

  const resolved = resolveZoomToAvoidPillOverlap(
    map,
    bounds,
    [universityCoordinate, campusCoordinate],
    fitPadding,
    routeMidpoint,
    cameraZoom,
    ROUTE_ZOOM_MAX,
    routeMidpoint,
  )

  return {
    cameraCenter: routeMidpoint,
    cameraZoom: resolved.zoom,
    flyPadding: fitPadding,
  }
}

function buildMarkerBounds(markers: LocationMapHeroMarker[]): maplibregl.LngLatBounds {
  const bounds = new maplibregl.LngLatBounds()

  for (const marker of markers) {
    bounds.extend([marker.longitude, marker.latitude])
  }

  if (markers.length === 1) {
    const [marker] = markers
    const pad = 0.0015

    bounds.extend([marker.longitude - pad, marker.latitude - pad])
    bounds.extend([marker.longitude + pad, marker.latitude + pad])
  }

  return bounds
}

function fitCameraToMarkers(
  map: maplibregl.Map,
  markers: LocationMapHeroMarker[],
  fitPadding: MapBoundsPadding,
  zoom: number,
) {
  const camera = map.cameraForBounds(buildMarkerBounds(markers), {
    padding: fitPadding,
    maxZoom: zoom + 0.25,
  })

  if (!camera?.center) {
    return null
  }

  return {
    cameraCenter: camera.center,
    cameraZoom: camera.zoom ?? zoom,
  }
}

function computeHeroMapCamera(
  map: maplibregl.Map,
  {
    center,
    mapCameraPadding,
    markers,
    routeMarker,
    universityCoordinate,
    zoom,
  }: HeroMapCameraProps,
) {
  const container = map.getContainer()
  const structuralPadding = getResponsiveMapFocusPadding(container, mapCameraPadding)
  const width = container.clientWidth
  const effectiveZoom = zoom + getDesktopMapZoomOffset(width)
  const fallbackCenter = normalizeLngLat(center, [0, 0])
  let fitPadding = structuralPadding
  let cameraCenter: LngLat = fallbackCenter
  let cameraZoom = effectiveZoom
  let usedBoundsFit = false

  const shouldFitRoute = Boolean(routeMarker && universityCoordinate)
  let routeFlyPadding = structuralPadding

  if (shouldFitRoute && routeMarker && universityCoordinate) {
    const campusCoordinate: LngLat = [routeMarker.longitude, routeMarker.latitude]
    const routePadding =
      width >= DESKTOP_MAP_BREAKPOINT_PX
        ? addMarkerLabelPadding(structuralPadding, width)
        : addRouteMarkerLabelPadding(structuralPadding, width)
    const routeCamera = getRouteCamera(
      map,
      universityCoordinate,
      campusCoordinate,
      routePadding,
      effectiveZoom,
    )

    cameraCenter = normalizeLngLat(routeCamera.cameraCenter, fallbackCenter)
    cameraZoom = routeCamera.cameraZoom
    routeFlyPadding = routeCamera.flyPadding
    fitPadding = routeFlyPadding
    usedBoundsFit = true
  } else if (markers.length === 1 && markers[0] && width < DESKTOP_MAP_BREAKPOINT_PX) {
    cameraCenter = [markers[0].longitude, markers[0].latitude]
    usedBoundsFit = true
  } else if (markers.length > 0 && canFitMapBounds(width, structuralPadding)) {
    fitPadding = addMarkerLabelPadding(structuralPadding, width)
    const fitted = fitCameraToMarkers(map, markers, fitPadding, effectiveZoom)

    if (fitted) {
      cameraCenter = normalizeLngLat(fitted.cameraCenter, fallbackCenter)
      cameraZoom = fitted.cameraZoom
      usedBoundsFit = true
    } else if (markers.length === 1 && markers[0]) {
      cameraCenter = [markers[0].longitude, markers[0].latitude]
      fitPadding =
        width < DESKTOP_MAP_BREAKPOINT_PX
          ? {
              ...structuralPadding,
              left: 32,
              right: 32,
            }
          : structuralPadding
    } else {
      cameraCenter = fallbackCenter
    }
  } else {
    cameraCenter = fallbackCenter
  }

  if (!usedBoundsFit) {
    cameraCenter = normalizeLngLat(
      applyCenterLngOffset(cameraCenter, mapCameraPadding),
      fallbackCenter,
    )
  }

  return {
    cameraCenter,
    cameraZoom,
    cameraStateKey: getCameraStateKey(cameraCenter, cameraZoom),
    padding: shouldFitRoute ? routeFlyPadding : usedBoundsFit ? ZERO_MAP_PADDING : fitPadding,
  }
}

function publishCameraDebugState(
  map: maplibregl.Map,
  cameraZoom: number,
  requestedZoom: number,
) {
  const section = map.getContainer().closest<HTMLElement>('[data-location-map-hero-section]')
  if (!section) {
    return
  }

  section.dataset.mapZoom = cameraZoom.toFixed(2)
  section.dataset.requestedZoom = requestedZoom.toFixed(2)
}

function fitHeroMapCamera(
  map: maplibregl.Map,
  props: HeroMapCameraProps,
  animated: boolean,
  onComplete?: () => void,
) {
  map.resize()
  map.setPadding(ZERO_MAP_PADDING)
  const { cameraCenter, cameraZoom, padding } = computeHeroMapCamera(map, props)
  const duration = animated ? MAP_TRANSITION_MS : 0

  map.stop()
  map.flyTo({
    center: cameraCenter,
    duration,
    essential: true,
    padding,
    zoom: cameraZoom,
  })

  const handleComplete = () => {
    publishCameraDebugState(map, map.getZoom(), props.zoom)
    onComplete?.()
  }

  if (duration === 0) {
    handleComplete()
    return
  }

  map.once('moveend', handleComplete)
}

function LocationMapHeroMapCamera({
  center,
  mapCameraPadding,
  markers,
  onCameraAnimationComplete,
  preserveUserZoom = false,
  routeMarker,
  routeAnimationKey,
  universityCoordinate,
  zoom,
}: LocationMapHeroMapCameraProps) {
  const { isLoaded, map } = useMap()
  const cameraPropsRef = useRef<HeroMapCameraProps>({
    center,
    mapCameraPadding,
    markers,
    routeMarker,
    universityCoordinate,
    zoom,
  })
  const hasInitializedRef = useRef(false)
  const isCameraUpdateRef = useRef(false)
  const lastCameraStateKeyRef = useRef<string | null>(null)
  const lastRouteAnimationKeyRef = useRef<string | undefined>(undefined)
  const cameraRequestIdRef = useRef(0)
  const preserveUserZoomRef = useRef(preserveUserZoom)
  const onContainerRefitRef = useRef<() => void>(() => {})

  preserveUserZoomRef.current = preserveUserZoom

  useEffect(() => {
    cameraPropsRef.current = {
      center,
      mapCameraPadding,
      markers,
      routeMarker,
      universityCoordinate,
      zoom,
    }
  }, [center, mapCameraPadding, markers, routeMarker, universityCoordinate, zoom])

  onContainerRefitRef.current = () => {
    if (!map) return
    fitHeroMapCamera(map, cameraPropsRef.current, false)
  }

  useEffect(() => {
    if (!map || !isLoaded) return

    const requestId = cameraRequestIdRef.current + 1
    cameraRequestIdRef.current = requestId
    const { cameraStateKey } = computeHeroMapCamera(map, cameraPropsRef.current)
    const cameraChanged = lastCameraStateKeyRef.current !== cameraStateKey
    const routeChanged = lastRouteAnimationKeyRef.current !== routeAnimationKey
    const isFirstLoad = !hasInitializedRef.current

    if (
      shouldSkipCameraFit({
        isFirstLoad,
        isRouteChange: routeChanged,
        isViewChange: cameraChanged,
        preserveUserZoom: preserveUserZoomRef.current,
      })
    ) {
      return
    }

    lastCameraStateKeyRef.current = cameraStateKey
    lastRouteAnimationKeyRef.current = routeAnimationKey

    const animated = hasInitializedRef.current && (cameraChanged || routeChanged)
    hasInitializedRef.current = true
    isCameraUpdateRef.current = true

    fitHeroMapCamera(map, cameraPropsRef.current, animated, () => {
      if (requestId !== cameraRequestIdRef.current) {
        return
      }

      isCameraUpdateRef.current = false
      if (routeAnimationKey) {
        onCameraAnimationComplete?.(routeAnimationKey)
      }
    })
  }, [
    center,
    isLoaded,
    map,
    mapCameraPadding,
    markers,
    onCameraAnimationComplete,
    routeAnimationKey,
    routeMarker,
    universityCoordinate,
    zoom,
  ])

  useLocationMapContainerResizeObserver({
    isCameraUpdateRef,
    isLoaded,
    map,
    onRefitRef: onContainerRefitRef,
    preserveUserZoom,
  })

  return null
}

function LocationMapHeroCampusRoute({
  campusMarker,
  direction,
  onAnimationComplete,
  routeAnimationKey,
  universityCoordinate,
}: {
  campusMarker: {
    latitude: number
    longitude: number
  }
  direction: LocationMapHeroRouteDirection
  onAnimationComplete?: (animationKey: string) => void
  routeAnimationKey: string
  universityCoordinate: LngLat
}) {
  const [progress, setProgress] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 1
      : 0,
  )
  const animationFrameRef = useRef<number | null>(null)
  const campusCoordinate: LngLat = [campusMarker.longitude, campusMarker.latitude]
  const from: LngLat =
    direction === 'campus-to-university'
      ? campusCoordinate
      : universityCoordinate
  const to: LngLat =
    direction === 'campus-to-university'
      ? universityCoordinate
      : campusCoordinate

  useEffect(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onAnimationComplete?.(routeAnimationKey)
      return
    }

    const startTime = performance.now()
    const animate = (timestamp: number) => {
      const nextProgress = Math.min(1, (timestamp - startTime) / MAP_TRANSITION_MS)
      setProgress(nextProgress)

      if (nextProgress >= 1) {
        animationFrameRef.current = null
        onAnimationComplete?.(routeAnimationKey)
        return
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [onAnimationComplete, routeAnimationKey])

  const arcData = useMemo(
    () => [
      {
        id: 'campus-route',
        from,
        to,
      },
    ],
    [from, to],
  )

  return (
    <MapArc
      curvature={0.25}
      data={arcData}
      interactive={false}
      progress={progress}
      paint={{
        'line-color': '#000000',
        'line-width': 2,
        'line-opacity': 0.75,
        'line-dasharray': [1, 2],
      }}
    />
  )
}

function LocationMapHeroCampusPin({
  card,
  hoverCardId,
  isActive,
  isSelected,
  marker,
  onActivate,
  onDeactivate,
  useCompactLabels,
}: {
  card: MapLocationCardData
  hoverCardId: string
  isActive: boolean
  isSelected: boolean
  marker: LocationMapHeroMarker
  onActivate: () => void
  onDeactivate: () => void
  useCompactLabels: boolean
}) {
  const closeLabel = useMessages().common.close
  const anchorRef = useRef<HTMLDivElement>(null)
  const deactivateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleActivate = () => {
    if (deactivateTimeoutRef.current) {
      clearTimeout(deactivateTimeoutRef.current)
      deactivateTimeoutRef.current = null
    }

    onActivate()
  }

  const handleDeactivate = () => {
    deactivateTimeoutRef.current = setTimeout(() => {
      onDeactivate()
    }, 120)
  }

  const handleClose = () => {
    if (deactivateTimeoutRef.current) {
      clearTimeout(deactivateTimeoutRef.current)
      deactivateTimeoutRef.current = null
    }

    onDeactivate()
  }

  useEffect(() => {
    return () => {
      if (deactivateTimeoutRef.current) {
        clearTimeout(deactivateTimeoutRef.current)
      }
    }
  }, [])

  const displayName = resolveMapMarkerDisplayLabel(marker.name, useCompactLabels)

  return (
    <>
      <MapMarker anchor="bottom" latitude={marker.latitude} longitude={marker.longitude}>
        <MarkerContent className="pointer-events-auto">
          <div
            ref={anchorRef}
            className="relative flex flex-col items-center"
            data-location-map-pin-active={isActive ? 'true' : undefined}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                handleDeactivate()
              }
            }}
            onMouseEnter={() => handleActivate()}
            onMouseLeave={() => handleDeactivate()}
          >
            <button
              aria-controls={hoverCardId}
              aria-expanded={isActive}
              aria-label={`View ${marker.name} details`}
              aria-pressed={isSelected}
              className={cn(
                MARKER_PILL_CLASS,
                'cursor-pointer transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
                (isActive || isSelected) && 'ring-2 ring-turquoise/40',
              )}
              onClick={() => handleActivate()}
              type="button"
            >
              <MapPin aria-hidden className="size-4 shrink-0 text-[#B4F0DD]" />
              <span className="truncate" title={marker.name}>
                {displayName}
              </span>
            </button>
          </div>
        </MarkerContent>
      </MapMarker>

      {isActive ? (
        <MapHoverCardPortal
          anchorRef={anchorRef}
          isActive={isActive}
          onClose={handleClose}
          onFocus={() => handleActivate()}
          onMouseEnter={() => handleActivate()}
          onMouseLeave={() => handleDeactivate()}
          positionDeps={[marker.latitude, marker.longitude]}
        >
          <LocationMapHoverCard
            card={card}
            className="relative z-[9999]"
            closeLabel={closeLabel}
            id={hoverCardId}
            onClose={handleClose}
          />
        </MapHoverCardPortal>
      ) : null}
    </>
  )
}

function LocationMapHeroUniversityPin({
  coordinate,
  isActive,
  onActivate,
  onDeactivate,
  universityName,
  useCompactLabels,
}: {
  coordinate: LngLat
  isActive: boolean
  onActivate: () => void
  onDeactivate: () => void
  universityName: string
  useCompactLabels: boolean
}) {
  const [longitude, latitude] = coordinate
  const displayName = resolveMapMarkerDisplayLabel(universityName, useCompactLabels)

  return (
    <MapMarker anchor="bottom" latitude={latitude} longitude={longitude}>
      <MarkerContent className="pointer-events-auto">
        <div
          className="relative flex flex-col items-center"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              onDeactivate()
            }
          }}
          onFocus={() => onActivate()}
          onMouseEnter={() => onActivate()}
          onMouseLeave={() => onDeactivate()}
        >
          <button
            aria-expanded={isActive}
            aria-label={`View ${universityName} on map`}
            className={cn(
              MARKER_PILL_CLASS,
              'cursor-pointer transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
              isActive && 'ring-2 ring-turquoise/40',
            )}
            type="button"
          >
            <BookOpen aria-hidden className="size-4 shrink-0 text-[#B4F0DD]" />
            <span className="truncate" title={universityName}>
              {displayName}
            </span>
          </button>

          {isActive ? (
            <p
              className="pointer-events-none absolute bottom-[calc(100%+0.5rem)] left-1/2 z-50 w-max max-w-[12rem] -translate-x-1/2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-center font-outfit text-sm font-medium text-dark-800 shadow-[0px_6px_20px_0px_rgba(0,33,64,0.12)]"
              role="tooltip"
            >
              {universityName}
            </p>
          ) : null}
        </div>
      </MarkerContent>
    </MapMarker>
  )
}

export function LocationMapHeroMap({
  center,
  fallbackImage,
  fallbackImageAlt,
  locationCards,
  mapCameraPadding,
  mapControlLabels,
  markers,
  routeMarker,
  routeAnimationId,
  routeDirection,
  selectedCampusSlug,
  universityCoordinate,
  universityName,
  zoom,
}: LocationMapHeroMapProps) {
  const [mapError, setMapError] = useState(false)
  const [activeCampusSlug, setActiveCampusSlug] = useState<string | null>(null)
  const [isUniversityActive, setIsUniversityActive] = useState(false)
  const [preserveUserZoom, setPreserveUserZoom] = useState(false)
  const [completedRouteAnimationKey, setCompletedRouteAnimationKey] = useState<string | null>(
    null,
  )
  const [completedCameraAnimationKey, setCompletedCameraAnimationKey] = useState<string | null>(
    null,
  )
  const useCompactLabels = useCompactMapMarkerLabels()
  const hoverCardIdPrefix = useId()
  const [longitude, latitude] = center

  const locationCardsBySlug = useMemo(
    () => new Map(locationCards.map((card) => [card.slug, card])),
    [locationCards],
  )

  const markerLocations = useMemo(
    () =>
      markers
        .map((marker) => ({
          marker,
          card: locationCardsBySlug.get(marker.slug),
        }))
        .filter(
          (entry): entry is { marker: LocationMapHeroMarker; card: MapLocationCardData } =>
            Boolean(entry.card),
        ),
    [locationCardsBySlug, markers],
  )

  useEffect(() => {
    setPreserveUserZoom(false)
    setActiveCampusSlug(null)
    setIsUniversityActive(false)
  }, [routeAnimationId, routeDirection, selectedCampusSlug, universityCoordinate])

  const showRoute = Boolean(routeMarker && universityCoordinate)
  const resolvedRouteDirection = routeDirection ?? 'university-to-campus'
  const routeAnimationKey =
    showRoute && routeAnimationId !== undefined && routeMarker && universityCoordinate
      ? [
          routeAnimationId,
          resolvedRouteDirection,
          universityCoordinate[0],
          universityCoordinate[1],
          routeMarker.longitude,
          routeMarker.latitude,
        ].join(':')
      : undefined
  const isRouteAnimating = Boolean(
    routeAnimationKey &&
      (completedRouteAnimationKey !== routeAnimationKey ||
        completedCameraAnimationKey !== routeAnimationKey),
  )
  const hideUniversityPin =
    isRouteAnimating && resolvedRouteDirection === 'campus-to-university'
  const hideCampusPin = isRouteAnimating && resolvedRouteDirection === 'university-to-campus'

  const handleUserNavigate = useCallback(() => {
    setPreserveUserZoom(true)
  }, [])
  const handleCameraAnimationComplete = useCallback((animationKey: string) => {
    setCompletedCameraAnimationKey(animationKey)
  }, [])
  const handleRouteAnimationComplete = useCallback((animationKey: string) => {
    setCompletedRouteAnimationKey(animationKey)
  }, [])

  if (mapError) {
    return (
      <Image
        alt={fallbackImageAlt}
        className="object-cover object-center"
        fill
        priority
        sizes="100vw"
        src={fallbackImage}
      />
    )
  }

  return (
    <MapCanvas
      attributionControl={{ compact: false }}
      boxZoom={false}
      center={[longitude, latitude]}
      className={cn('absolute inset-0 size-full bg-cream')}
      cooperativeGestures
      doubleClickZoom
      dragPan
      dragRotate={false}
      interactive
      keyboard
      maxZoom={14}
      minZoom={4}
      scrollZoom={false}
      theme="light"
      touchPitch={false}
      touchZoomRotate
      zoom={zoom}
    >
      <LocationMapHeroMapErrorWatcher onError={() => setMapError(true)} />
      <LocationMapNavigationHandlers onUserNavigate={handleUserNavigate} />
      <LocationMapHeroMapCamera
        center={center}
        mapCameraPadding={mapCameraPadding}
        markers={markers}
        onCameraAnimationComplete={handleCameraAnimationComplete}
        preserveUserZoom={preserveUserZoom}
        routeAnimationKey={routeAnimationKey}
        routeMarker={routeMarker}
        universityCoordinate={universityCoordinate}
        zoom={zoom}
      />
      <LocationMapHeroMapStyle />
      <MapContentChrome
        maxZoom={14}
        minZoom={4}
        onZoomAdjust={handleUserNavigate}
        zoomGroupLabel={mapControlLabels.zoomGroupLabel}
        zoomInLabel={mapControlLabels.zoomInLabel}
        zoomOutLabel={mapControlLabels.zoomOutLabel}
      />
      {showRoute && universityCoordinate && routeMarker && routeAnimationKey ? (
        <LocationMapHeroCampusRoute
          campusMarker={routeMarker}
          direction={resolvedRouteDirection}
          key={routeAnimationKey}
          onAnimationComplete={handleRouteAnimationComplete}
          routeAnimationKey={routeAnimationKey}
          universityCoordinate={universityCoordinate}
        />
      ) : null}
      {universityCoordinate && universityName && !hideUniversityPin ? (
        <LocationMapHeroUniversityPin
          coordinate={universityCoordinate}
          isActive={isUniversityActive}
          onActivate={() => {
            setIsUniversityActive(true)
            setActiveCampusSlug(null)
          }}
          onDeactivate={() => setIsUniversityActive(false)}
          universityName={universityName}
          useCompactLabels={useCompactLabels}
        />
      ) : null}
      {markerLocations.map(({ card, marker }) => {
        if (hideCampusPin && marker.slug === selectedCampusSlug) {
          return null
        }

        return (
          <LocationMapHeroCampusPin
            card={card}
            key={marker.slug}
            hoverCardId={`${hoverCardIdPrefix}-${marker.slug}`}
            isActive={activeCampusSlug === marker.slug}
            isSelected={selectedCampusSlug === marker.slug}
            marker={marker}
            onActivate={() => {
              setActiveCampusSlug(marker.slug)
              setIsUniversityActive(false)
            }}
            onDeactivate={() =>
              setActiveCampusSlug((current) => (current === marker.slug ? null : current))
            }
            useCompactLabels={useCompactLabels}
          />
        )
      })}
    </MapCanvas>
  )
}
